<template>
  <div class="lab-dash">
    <header class="lab-dash-header">
      <div class="lab-dash-title">
        <h2>{{ headerTitle }}</h2>
        <span v-if="statusBadge" :class="['lab-dash-badge', statusBadge.kind]">
          <span v-if="statusBadge.kind === 'live' || statusBadge.kind === 'playback'" class="pulse"></span>{{ statusBadge.label }}
        </span>
        <span v-if="isReplay" class="lab-dash-badge replay" v-tooltip="'A recorded test played back through the live pipeline'">REPLAY</span>
      </div>
      <div class="lab-dash-header-right">
        <button v-if="status === 'playback'" type="button" class="lab-dash-hbtn pointer" @click="showAll">Show all</button>
        <button v-else-if="status === 'file' && run" type="button" class="lab-dash-hbtn pointer"
          v-tooltip="'Play this file back as if it were streaming'" @click="replay">▶ Play as stream</button>
        <button v-if="canReport" type="button" :class="['lab-dash-hbtn', 'pointer', { primary: !isLive }]"
          v-tooltip="'The lab\'s Performance Test Report, from the readings received'" @click="openReport">Report</button>
        <span class="lab-dash-fresh">{{ freshnessText }}</span>
        <span v-if="provenance" class="lab-dash-provenance" v-tooltip="'Lab PC that streamed this test'">{{ provenance }}</span>
        <button type="button" class="lab-dash-close pointer" aria-label="Close dashboard" @click="$emit('close')">&times;</button>
      </div>
    </header>

    <div v-if="stopBanner" :class="['lab-dash-stopbar', stopBanner.kind]" role="status">
      <span class="msg">{{ stopBanner.text }}</span>
      <span v-if="reportState === 'building'" class="hint">Preparing the performance report…</span>
      <span v-else-if="reportState === 'error'" class="hint error">{{ reportError }}</span>
      <template v-else-if="reportState === 'ready'">
        <button type="button" class="stopbar-btn primary pointer" @click="openReport">View report</button>
        <button type="button" class="stopbar-btn pointer" @click="downloadReport">Download Excel</button>
      </template>
      <button type="button" class="stopbar-dismiss pointer" aria-label="Dismiss" @click="bannerDismissed = true">&times;</button>
    </div>

    <div v-if="state === 'loading'" class="lab-dash-empty">Loading test data…</div>
    <div v-else-if="state === 'error' && blocked" class="lab-dash-blocked" role="status">
      <h3>{{ blocked.title }}</h3>
      <p>{{ blocked.text }}</p>
      <button type="button" class="blocked-btn pointer" @click="$emit('close')">Close</button>
    </div>
    <div v-else-if="state === 'error'" class="lab-dash-empty error">{{ error }}</div>

    <template v-else>
      <div class="lab-dash-meta">
        <span v-for="chip in metaChips" :key="chip.label" class="meta-chip">
          <span class="lbl">{{ chip.label }}:</span><span class="val">{{ chip.value }}</span>
        </span>
      </div>

      <div class="lab-dash-tabs">
        <div v-show="view === 'chart'" class="tab-list">
          <div
            v-for="tab in tabs"
            :key="tab"
            :class="['tab', { active: tab === currentTab }]"
            @click="switchTab(tab)"
          >{{ tab }}</div>
        </div>
        <div v-if="view === 'readings'" class="tab-list-title">All parameters · {{ channelCount }}</div>
        <div v-else-if="view === 'details'" class="tab-list-title">Test details · report lines 3–25</div>
        <div class="lab-dash-tools">
          <div class="view-switch" role="group" aria-label="View">
            <button type="button" :class="['view-btn', { on: view === 'chart' }]" :aria-pressed="view === 'chart'" @click="setView('chart')">Chart</button>
            <button type="button" :class="['view-btn', { on: view === 'readings' }]" :aria-pressed="view === 'readings'" @click="setView('readings')">Readings</button>
            <button type="button" :class="['view-btn', { on: view === 'details' }]" :aria-pressed="view === 'details'" @click="setView('details')">Details</button>
          </div>
          <div v-show="view === 'chart'" class="lab-dash-range">
            <button type="button" :class="['range-btn', { on: rangeMode === 'all' }]" @click="setRangeMode('all')">Whole test</button>
            <button type="button" :class="['range-btn', { on: rangeMode === 'recent' }]" @click="setRangeMode('recent')">Last 15 min</button>
          </div>
        </div>
      </div>

      <div v-show="view === 'chart'" class="lab-dash-main">
        <div class="lab-dash-chart-area">
          <div class="lab-dash-chart-wrap">
            <div ref="chart" class="lab-dash-chart"></div>
            <div v-if="!legendItems.length" class="lab-dash-empty overlay">
              {{ currentTab === 'CUSTOM' ? 'Pick parameters below to plot them' : 'No readings for this view yet' }}
            </div>
          </div>
          <div class="lab-dash-axes">
            <span class="label">Y Axes</span>
            <span
              v-for="axis in axisToggles"
              :key="axis.key"
              :class="['axis-btn', axis.on ? 'on' : 'off']"
              :style="axis.on ? { background: axis.color, borderColor: axis.color } : {}"
              @click="toggleAxis(axis.key)"
            >{{ axis.label }}</span>
          </div>
        </div>

        <aside class="lab-dash-legend">
          <div class="legend-header">
            <span>{{ following ? 'Latest Values' : `Values at ${cursorClock}` }}</span>
            <span class="rows">{{ rowCount.toLocaleString() }} rows</span>
          </div>
          <div class="legend-table">
            <div v-for="item in legendItems" :key="item.key" :class="['legend-row', { flash: flashKeys[item.key] }]">
              <div class="legend-dot" :style="{ background: item.color }"></div>
              <div class="legend-name" :title="item.name">{{ item.name }}</div>
              <div class="legend-val">{{ item.value }}</div>
              <div class="legend-unit">{{ item.unit }}</div>
            </div>
            <div v-if="!legendItems.length" class="legend-none">No parameters with data</div>
          </div>
        </aside>
      </div>

      <lab-details
        v-if="view === 'details'"
        :metadata="metadata"
        :running="isStreaming"
      />

      <lab-readings
        v-if="view === 'readings'"
        :groups="readingGroups"
        :time="cursorClock"
        :date="cursorDate"
        :following="following"
        :streaming="isStreaming"
      />

      <div v-if="view === 'chart' && currentTab === 'CUSTOM'" class="lab-dash-params">
        <h3>Select Parameters to Plot</h3>
        <div class="param-groups">
          <div v-for="group in paramGroups" :key="group.name">
            <div class="param-grp-title">{{ group.name }}</div>
            <label
              v-for="p in group.params"
              :key="p.key"
              class="param-check"
              :class="{ inactive: !p.active }"
            >
              <input type="checkbox" :checked="customKeys.includes(p.key)" @change="toggleCustom(p.key, $event.target.checked)" />
              {{ p.name }} <span class="pu">{{ p.unit }}</span>
              <span v-if="!p.active" class="pu">(no data)</span>
            </label>
          </div>
        </div>
      </div>

      <lab-timeline
        v-if="rowCount && view !== 'details'"
        :count="rowCount"
        :index="cursorAt"
        :start="clockAt(0)"
        :end="clockAt(rowCount - 1)"
        :current="cursorClock"
        :following="following"
        :streaming="isStreaming"
        :spark="spark"
        @seek="seek"
        @live="goLive"
      />
    </template>

    <lab-test-report
      v-if="reportOpen"
      :report="report"
      :state="reportState"
      :error="reportError"
      :title="headerTitle"
      :stream-state="streamState"
      :newer-readings="reportState === 'ready' ? rowCount - reportRows : 0"
      @close="reportOpen = false"
      @download="downloadReport"
      @rebuild="prepareReport"
    />
  </div>
</template>

<script>
import {
  AXIS_GROUPS,
  AXIS_ORDER,
  TABS,
  CUSTOM_COLORS,
  GROUP_ORDER,
  getAxisGroup,
  resolvePreset,
  buildLayout,
  groupChannels
} from './channelPresets'
import { buildDataset, createDataset, appendRows, latestValue, formatReading } from './telemetryDataset'
import { getRun, getRunData } from './telemetryApi'
import LabTestReport from './LabTestReport.vue'
import LabTimeline from './LabTimeline.vue'
import LabReadings from './LabReadings.vue'
import LabDetails from './LabDetails.vue'

const RECENT_WINDOW_MS = 15 * 60 * 1000
const FLASH_MS = 700
// A live test with no new readings for this long is treated as stopped: the
// lab PC, its agent or the ingest service has gone quiet. The calorimeter
// logs every 7 s, so this is several missed readings.
const STALL_MS = 30 * 1000
// An uploaded file played back as a stream finishes in about three minutes
// whatever its length: one chunk per second, 180 chunks.
const PLAYBACK_TICK_MS = 1000
const PLAYBACK_TICKS = 180
// Phone-width screens get the compact chart and stacked panels
const COMPACT_QUERY = '(max-width: 640px)'
// Points in the timeline's background trace
const SPARK_POINTS = 300

// Plotly is large, so it is fetched only when a dashboard is first opened
// rather than being bundled into every page of the app.
let plotlyPromise = null
const loadPlotly = () => {
  if (!plotlyPromise) {
    plotlyPromise = import(/* webpackChunkName: "plotly" */ 'plotly.js-dist-min')
      .then(m => m.default || m)
  }
  return plotlyPromise
}

// What to tell someone the service turned away
const ACCESS_REMOVED = {
  title: 'No access',
  text: 'You no longer have access to this test. Ask a factory admin if you still need it.'
}
const accessBlock = (err) => {
  if (err?.code === 'not_shared') {
    return { title: 'No access', text: 'This test belongs to a factory you are not a member of.' }
  }
  if (err?.code === 'session_expired' || err?.code === 'sign_in_required') {
    return { title: 'Session expired', text: 'Sign in to DTX again to see this test.' }
  }
  return null
}

// The report builder brings exceljs; it too is fetched when first needed.
let reportLibPromise = null
const loadReportLib = () => {
  if (!reportLibPromise) reportLibPromise = import(/* webpackChunkName: "lab-report" */ '@/utils/testReport')
  return reportLibPromise
}

export default {
  name: 'LabTelemetryViewer',
  components: { LabTestReport, LabTimeline, LabReadings, LabDetails },
  props: {
    // A test run on the telemetry ingest service; polled while it is live.
    runId: { type: String, default: null },
    // A run already in memory, e.g. an uploaded .mdb read in the browser.
    run: { type: Object, default: null },
    machineName: { type: String, default: '' },
    pollMs: { type: Number, default: 3000 },
    // With `run`: reveal the file's readings progressively instead of all at
    // once. A fallback for demos without a lab agent; live runs never use it.
    playback: { type: Boolean, default: false }
  },
  data () {
    return {
      state: 'loading',
      error: '',
      status: null,
      runInfo: null,
      dsVersion: 0,
      currentTab: 'PVC',
      tabs: TABS,
      activeAxes: Object.keys(AXIS_GROUPS),
      customKeys: [],
      rangeMode: 'all',
      lastReceivedAt: null,
      now: Date.now(),
      flashKeys: {},
      playbackDone: 0,
      playbackTotal: 0,
      // The Performance Test Report of a streamed test. `report` is frozen so
      // Vue leaves the exceljs workbook inside it unobserved.
      report: null,
      reportState: 'idle',
      reportError: '',
      reportRows: 0,
      reportOpen: false,
      endedWhileWatching: false,
      bannerDismissed: false,
      view: 'chart',
      // Set when the service turns this person away: { title, text }
      blocked: null,
      // The reading the time cursor is on; null follows the newest one
      cursorIndex: null,
      compact: false
    }
  },
  computed: {
    isLive () {
      return this.status === 'live'
    },
    // Readings still arriving, from the ingest service or from a playback.
    // Governs presentation only; polling is driven by isLive alone.
    isStreaming () {
      return this.status === 'live' || this.status === 'playback'
    },
    // The dataset sits outside Vue's reactivity, so anything read from it has
    // to depend on dsVersion or it keeps the value from the first render,
    // which happens before the data has loaded.
    metadata () {
      // eslint-disable-next-line no-unused-expressions
      this.dsVersion
      return (this.ds && this.ds.meta) || this.runInfo?.metadata || {}
    },
    isReplay () {
      return this.metadata['agent.mode'] === 'replay'
    },
    headerTitle () {
      const name = this.metadata.TestName || this.runInfo?.test_name || 'Test'
      return this.machineName ? `${name} · ${this.machineName}` : name
    },
    statusBadge () {
      if (this.status === 'live') return { kind: 'live', label: 'LIVE' }
      if (this.status === 'completed') return { kind: 'done', label: 'COMPLETED' }
      if (this.status === 'failed') return { kind: 'failed', label: 'FAILED' }
      if (this.status === 'file') return { kind: 'file', label: 'UPLOADED FILE' }
      if (this.status === 'playback') return { kind: 'playback', label: 'PLAYBACK' }
      return null
    },
    freshnessText () {
      if (this.status === 'playback') {
        return `Playing back · ${this.playbackDone.toLocaleString()} of ${this.playbackTotal.toLocaleString()} readings`
      }
      if (!this.isLive) return ''
      if (!this.lastReceivedAt) return 'Waiting for readings…'
      const secs = Math.max(0, Math.round((this.now - this.lastReceivedAt) / 1000))
      if (secs > 30) return `No new readings for ${secs}s`
      return secs < 2 ? 'Receiving · just now' : `Receiving · ${secs}s ago`
    },
    // Reports are for streamed tests; an uploaded file keeps the card's own
    // Report button.
    canReport () {
      return Boolean(this.runId) && this.rowCount > 0
    },
    streamStalled () {
      return this.isLive && this.lastReceivedAt !== null && this.now - this.lastReceivedAt > STALL_MS
    },
    streamState () {
      if (this.isLive) return this.streamStalled ? 'stalled' : 'live'
      return this.status
    },
    // The lab's clock time of the newest reading held
    lastReadingClock () {
      // eslint-disable-next-line no-unused-expressions
      this.dsVersion
      const t = this.ds?.timestamps[this.ds.timestamps.length - 1]
      return t ? t.slice(11) : ''
    },
    // Shown when a stream stops while it is being watched: the test ended,
    // or readings stopped arriving. Either way a report is built from what
    // arrived, ready to view or download.
    stopBanner () {
      if (!this.runId || this.bannerDismissed || this.reportOpen || !this.rowCount) return null
      if (this.endedWhileWatching && this.status === 'completed') {
        return { kind: 'ended', text: `Test ended at ${this.lastReadingClock}. Its performance report is ready.` }
      }
      if (this.endedWhileWatching && this.status === 'failed') {
        return { kind: 'stalled', text: `The stream ended with an error at ${this.lastReadingClock}. A report of the readings received is ready.` }
      }
      if (this.streamStalled) {
        return { kind: 'stalled', text: `No readings since ${this.lastReadingClock}; the lab PC may have stopped sending. A report of the readings so far is ready.` }
      }
      return null
    },
    provenance () {
      const agent = this.runInfo?.agent
      if (!agent?.hostname) return ''
      const ip = agent.ip_addresses?.[0]
      return ip ? `${agent.hostname} · ${ip}` : agent.hostname
    },
    rowCount () {
      // eslint-disable-next-line no-unused-expressions
      this.dsVersion
      return this.ds ? this.ds.timestamps.length : 0
    },
    channelCount () {
      // eslint-disable-next-line no-unused-expressions
      this.dsVersion
      return this.ds ? this.ds.keys.length : 0
    },
    following () {
      return this.cursorIndex === null
    },
    cursorAt () {
      const last = Math.max(0, this.rowCount - 1)
      return this.following ? last : Math.min(this.cursorIndex, last)
    },
    cursorClock () {
      return this.clockAt(this.cursorAt)
    },
    // "19 Aug 2026", from the reading's own date
    cursorDate () {
      // eslint-disable-next-line no-unused-expressions
      this.dsVersion
      const t = this.ds?.timestamps[this.cursorAt]
      if (!t) return ''
      return new Date(`${t.slice(0, 10)}T00:00:00Z`)
        .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    },
    // Every channel at the cursor, in the rig's groups
    readingGroups () {
      // eslint-disable-next-line no-unused-expressions
      this.dsVersion
      if (!this.ds) return []
      const i = this.cursorAt
      const channels = this.ds.keys.map(key => {
        const ch = this.ds.channels[key]
        return {
          key,
          name: ch.name,
          unit: ch.unit,
          group: ch.group,
          value: formatReading(this.ds.series[key][i]),
          active: this.ds.activeSet.has(key)
        }
      })
      return groupChannels(channels)
    },
    // The timeline's background trace: the unit's capacity when the rig logs
    // it, as that is what shows the ramp-up, trips and steady state
    spark () {
      // eslint-disable-next-line no-unused-expressions
      this.dsVersion
      if (!this.ds || !this.rowCount) return []
      const key = this.ds.keys.find(k => this.ds.channels[k].name === 'UUT Capacity' && this.ds.activeSet.has(k)) ||
        this.ds.activeKeys[0]
      if (!key) return []
      const series = this.ds.series[key]
      const stride = Math.max(1, Math.ceil(series.length / SPARK_POINTS))
      const points = []
      for (let i = 0; i < series.length; i += stride) points.push(series[i])
      return points
    },
    metaChips () {
      // eslint-disable-next-line no-unused-expressions
      this.dsVersion
      const m = this.metadata
      const chips = [
        { label: 'Test', value: m.TestName },
        { label: 'Unit', value: m.UnitType || m.ProductModel },
        { label: 'ID', value: m.TestID },
        { label: 'Mode', value: m.TestStyle || this.runInfo?.test_mode },
        { label: 'Indoor', value: m.IndoorEnteringDB ? `${m.IndoorEnteringDB}/${m.IndoorEnteringWB} °C` : null },
        { label: 'Outdoor', value: m.OutdoorEnteringDB ? `${m.OutdoorEnteringDB}/${m.OutdoorEnteringWB} °C` : null },
        { label: 'Ref', value: m.RefType },
        { label: 'Lab', value: m.LabName },
        { label: 'Interval', value: m.SampleInterval ? `${m.SampleInterval}s` : null },
        { label: 'Rows', value: this.ds ? this.ds.timestamps.length.toLocaleString() : null }
      ]
      return chips.filter(c => c.value)
    },
    params () {
      // eslint-disable-next-line no-unused-expressions
      this.dsVersion
      if (!this.ds) return []
      if (this.currentTab === 'CUSTOM') {
        return this.customKeys.map((key, i) => ({ key, color: CUSTOM_COLORS[i % CUSTOM_COLORS.length], dash: 'solid' }))
      }
      return resolvePreset(this.currentTab, this.ds, { live: this.isStreaming })
    },
    // Each plotted channel with its axis group; the chart and the legend both
    // read from here so they cannot disagree about what is on screen.
    plotted () {
      if (!this.ds) return []
      return this.params
        .filter(p => this.ds.channels[p.key])
        .map(p => {
          const ch = this.ds.channels[p.key]
          return { ...p, name: ch.name, unit: ch.unit, group: getAxisGroup(ch.name, ch.unit) }
        })
    },
    axisToggles () {
      const present = new Set(this.plotted.map(p => p.group))
      return AXIS_ORDER
        .filter(key => present.has(key))
        .map(key => ({ key, label: AXIS_GROUPS[key].label, color: AXIS_GROUPS[key].color, on: this.activeAxes.includes(key) }))
    },
    legendItems () {
      // eslint-disable-next-line no-unused-expressions
      this.dsVersion
      return this.plotted
        .filter(p => this.activeAxes.includes(p.group))
        .map(p => {
          const v = this.following ? latestValue(this.ds, p.key) : this.ds.series[p.key][this.cursorAt]
          return { key: p.key, name: p.name, color: p.color, unit: p.unit, value: v === null || v === undefined ? '--' : v.toFixed(2) }
        })
        .filter(item => item.value !== '--' || this.isStreaming)
    },
    paramGroups () {
      // eslint-disable-next-line no-unused-expressions
      this.dsVersion
      if (!this.ds) return []
      const groups = {}
      this.ds.keys.forEach(key => {
        const ch = this.ds.channels[key]
        const name = ch.group || 'Other'
        if (!groups[name]) groups[name] = []
        groups[name].push({ key, name: ch.name, unit: ch.unit, active: this.ds.activeSet.has(key) })
      })
      const order = GROUP_ORDER.filter(g => groups[g]).concat(Object.keys(groups).filter(g => !GROUP_ORDER.includes(g)))
      return order.map(name => ({
        name,
        params: groups[name].sort((a, b) => Number(b.active) - Number(a.active))
      }))
    }
  },
  watch: {
    streamStalled (stalled) {
      if (!stalled) return
      this.bannerDismissed = false
      this.prepareReport()
    }
  },
  created () {
    // The dataset holds tens of thousands of points; kept off Vue's reactivity
    // system on purpose, with dsVersion bumped when it changes.
    this.ds = null
    this.plotly = null
    this.chartReady = false
    this.pollTimer = null
    this.clockTimer = null
    this.polling = false
    this.programmaticRelayout = false
    this.resizeObserver = null
    this.playbackTimer = null
    this.playbackRows = null
    this.playbackPerTick = 1
    this.stepping = false
    this.customSeeded = false
    this.reportTemplate = null
    this.reportBuild = 0
    this.reportFor = null
    this.compactQuery = null
    this.cursorFrame = null
  },
  async mounted () {
    this.clockTimer = setInterval(() => { this.now = Date.now() }, 1000)
    if (window.matchMedia) {
      this.compactQuery = window.matchMedia(COMPACT_QUERY)
      this.compact = this.compactQuery.matches
      if (this.compactQuery.addEventListener) this.compactQuery.addEventListener('change', this.onCompactChange)
    }
    try {
      if (this.run && this.playback) {
        this.beginPlayback()
      } else if (this.run) {
        this.ds = buildDataset(this.run)
        this.status = 'file'
      } else if (this.runId) {
        await this.loadRun()
      } else {
        throw new Error('No test selected.')
      }
      this.seedCustomKeys()
      this.dsVersion++
      this.state = 'ready'
      await this.$nextTick()
      await this.render()
      this.watchChartSize()
      if (this.isLive) this.startPolling()
      if (this.status === 'playback') this.startPlaybackTimer()
    } catch (err) {
      console.error('Lab dashboard failed to load:', err)
      this.blocked = accessBlock(err)
      this.error = err?.message || 'Could not load this test.'
      this.state = 'error'
    }
  },
  beforeDestroy () {
    if (this.compactQuery && this.compactQuery.removeEventListener) {
      this.compactQuery.removeEventListener('change', this.onCompactChange)
    }
    if (this.cursorFrame) cancelAnimationFrame(this.cursorFrame)
    this.stopPolling()
    this.stopPlayback()
    clearInterval(this.clockTimer)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.$refs.chart && this.plotly) this.plotly.purge(this.$refs.chart)
  },
  methods: {
    async loadRun () {
      const [run, data] = await Promise.all([getRun(this.runId), getRunData(this.runId)])
      this.runInfo = run
      this.status = data.status || run.status
      this.ds = createDataset({ channels: run.channels, metadata: run.metadata })
      if (appendRows(this.ds, data.rows) > 0) this.lastReceivedAt = Date.now()
    },
    // Playback of an uploaded file: the readings are already complete, so they
    // are released a chunk per tick through the same append-and-draw path a
    // live test uses. It is a fallback for demos without a lab agent, and is
    // labelled PLAYBACK so it is not taken for a rig running now.
    beginPlayback () {
      this.stopPlayback()
      this.playbackRows = [...(this.run.rows || [])]
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      this.playbackTotal = this.playbackRows.length
      this.playbackPerTick = Math.max(1, Math.ceil(this.playbackTotal / PLAYBACK_TICKS))
      this.playbackDone = 0
      this.ds = createDataset(this.run)
      this.customSeeded = false
      this.cursorIndex = null
      this.status = 'playback'
      this.lastReceivedAt = null
      this.releasePlaybackRows(this.playbackPerTick)
    },
    releasePlaybackRows (count) {
      const end = Math.min(this.playbackDone + count, this.playbackTotal)
      appendRows(this.ds, this.playbackRows.slice(this.playbackDone, end))
      this.playbackDone = end
      this.lastReceivedAt = Date.now()
      this.seedCustomKeys()
      this.dsVersion++
    },
    startPlaybackTimer () {
      this.stopPlayback()
      this.playbackTimer = setInterval(this.playbackStep, PLAYBACK_TICK_MS)
    },
    stopPlayback () {
      if (this.playbackTimer) clearInterval(this.playbackTimer)
      this.playbackTimer = null
    },
    async playbackStep () {
      // A slow redraw must not let the next tick release a second chunk.
      if (this.stepping || this.status !== 'playback') return
      this.stepping = true
      try {
        const before = this.plotted.map(p => ({ key: p.key, v: latestValue(this.ds, p.key) }))
        this.releasePlaybackRows(this.playbackPerTick)
        this.flashChanged(before)
        await this.render()
        if (this.playbackDone >= this.playbackTotal) await this.finishPlayback()
      } finally {
        this.stepping = false
      }
    },
    async finishPlayback () {
      this.stopPlayback()
      this.status = 'file'
      await this.render()
    },
    async replay () {
      this.beginPlayback()
      await this.render()
      this.startPlaybackTimer()
    },
    async showAll () {
      this.stopPlayback()
      this.releasePlaybackRows(this.playbackTotal)
      await this.finishPlayback()
    },
    // The CUSTOM tab starts with the first ten recorded channels. A playback
    // starts empty, so this waits until channels have actually recorded.
    seedCustomKeys () {
      if (this.customSeeded || !this.ds.activeKeys.length) return
      this.customKeys = this.ds.activeKeys.slice(0, 10)
      this.customSeeded = true
    },
    startPolling () {
      this.stopPolling()
      this.pollTimer = setInterval(this.poll, this.pollMs)
    },
    stopPolling () {
      if (this.pollTimer) clearInterval(this.pollTimer)
      this.pollTimer = null
    },
    async poll () {
      // A slow response must not overlap the next tick and double-append.
      if (this.polling) return
      this.polling = true
      try {
        const data = await getRunData(this.runId, { since: this.ds.lastTimestamp })
        const before = this.plotted.map(p => ({ key: p.key, v: latestValue(this.ds, p.key) }))
        const appended = appendRows(this.ds, data.rows)
        if (appended > 0) {
          this.lastReceivedAt = Date.now()
          this.dsVersion++
          this.flashChanged(before)
          await this.render()
        }
        if (data.status && data.status !== 'live') {
          this.status = data.status
          this.stopPolling()
          this.endedWhileWatching = true
          this.bannerDismissed = false
          this.prepareReport()
          await this.render()
        }
      } catch (err) {
        // Losing access ends the session with this test; anything else is
        // retried on the next tick, and the freshness text tells the viewer if
        // readings stop arriving.
        if (err?.code === 'not_shared' || err?.status === 401) {
          this.loseAccess(err.code === 'not_shared' ? ACCESS_REMOVED : accessBlock(err))
          return
        }
        console.warn('Lab dashboard poll failed:', err)
      } finally {
        this.polling = false
      }
    },
    // Briefly highlights legend rows whose value just changed, so a live
    // reading visibly ticks over.
    flashChanged (before) {
      const flashed = {}
      before.forEach(({ key, v }) => {
        if (latestValue(this.ds, key) !== v) flashed[key] = true
      })
      if (!Object.keys(flashed).length) return
      this.flashKeys = flashed
      setTimeout(() => { this.flashKeys = {} }, FLASH_MS)
    },
    // Someone whose access ends while they watch keeps nothing of the test:
    // the readings, the report and the header's status all go.
    loseAccess (block) {
      this.stopPolling()
      this.reportOpen = false
      this.ds = null
      this.runInfo = null
      this.report = null
      this.status = null
      this.dsVersion++
      this.blocked = block
      this.state = 'error'
    },
    // Builds the report from the readings held now, unless the one already
    // built covers the same readings and outcome. Runs when the stream stops
    // and when the report is opened; a build overtaken by a newer one is
    // dropped.
    async prepareReport () {
      if (!this.canReport) return
      const complete = this.status === 'completed'
      const snapshot = this.cursorAt
      const key = `${this.rowCount}:${complete}:${snapshot}`
      if (this.reportFor === key && this.reportState !== 'error') return
      this.reportFor = key
      const build = ++this.reportBuild
      const rows = this.ds.timestamps.length
      const times = this.ds.timestamps.map(t => new Date(t.replace(' ', 'T') + 'Z'))
      this.reportState = 'building'
      this.reportError = ''
      try {
        const lib = await loadReportLib()
        if (!this.reportTemplate) this.reportTemplate = await lib.loadReportTemplate()
        const report = await lib.buildStreamedReport(this.reportTemplate.slice(0), {
          run: this.runInfo,
          times,
          series: this.ds.series,
          complete,
          snapshot
        })
        if (build !== this.reportBuild) return
        this.report = Object.freeze(report)
        this.reportRows = rows
        this.reportState = 'ready'
      } catch (err) {
        if (build !== this.reportBuild) return
        console.error('Lab report failed:', err)
        this.reportFor = null
        this.reportError = err?.message || 'Could not build the report.'
        this.reportState = 'error'
      }
    },
    openReport () {
      this.reportOpen = true
      this.prepareReport()
    },
    async downloadReport () {
      if (this.reportState !== 'ready') return
      const lib = await loadReportLib()
      lib.saveReport(this.report)
    },
    // Wall-clock time of a reading, "08:31:05"
    clockAt (i) {
      const t = this.ds?.timestamps[i]
      return t ? t.slice(11) : ''
    },
    // Moves the time cursor. Moving it to the newest reading resumes
    // following, so a live test keeps updating from there.
    seek (i) {
      if (!this.rowCount) return
      const index = Math.max(0, Math.min(Math.round(i), this.rowCount - 1))
      this.cursorIndex = index >= this.rowCount - 1 ? null : index
      this.drawCursor()
    },
    goLive () {
      this.cursorIndex = null
      this.drawCursor()
    },
    setView (view) {
      if (this.view === view) return
      this.view = view
      if (view === 'chart') this.$nextTick(this.render)
    },
    onCompactChange (e) {
      this.compact = e.matches
      this.render()
    },
    // A dotted line on the chart at the cursor, unless it is simply following
    // the newest reading
    cursorShapes () {
      if (this.following || !this.ds) return []
      const x = this.ds.timestamps[this.cursorAt]
      return [{
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: x,
        x1: x,
        y0: 0,
        y1: 1,
        line: { color: '#1e3a5f', width: 1.5, dash: 'dot' }
      }]
    },
    // Dragging the timeline fires far faster than the chart can redraw; one
    // redraw per frame is plenty
    drawCursor () {
      if (this.cursorFrame) return
      this.cursorFrame = requestAnimationFrame(() => {
        this.cursorFrame = null
        const el = this.$refs.chart
        if (!el || !this.chartReady || this.view !== 'chart') return
        this.programmaticRelayout = true
        this.plotly.relayout(el, { shapes: this.cursorShapes() })
          .catch(() => {})
          .finally(() => { this.programmaticRelayout = false })
      })
    },
    switchTab (tab) {
      this.currentTab = tab
      this.$nextTick(this.render)
    },
    toggleAxis (key) {
      this.activeAxes = this.activeAxes.includes(key)
        ? this.activeAxes.filter(k => k !== key)
        : [...this.activeAxes, key]
      this.$nextTick(this.render)
    },
    toggleCustom (key, on) {
      this.customKeys = on ? [...this.customKeys, key] : this.customKeys.filter(k => k !== key)
      this.$nextTick(this.render)
    },
    setRangeMode (mode) {
      this.rangeMode = mode
      this.applyRange()
    },
    // Plotly's `responsive` option only follows window resizes. The chart's
    // space also changes inside the page — the CUSTOM picker opening below it,
    // for one — and a plot left at its old size spills over its neighbours.
    watchChartSize () {
      const el = this.$refs.chart
      if (!el || typeof ResizeObserver === 'undefined') return
      this.resizeObserver = new ResizeObserver(() => {
        // Plotly rejects a resize of a plot that is no longer displayed, as
        // when the dashboard is torn down mid-resize; there is nothing to fit.
        if (this.chartReady && this.plotly) this.plotly.Plots.resize(el).catch(() => {})
      })
      this.resizeObserver.observe(el)
    },
    async render () {
      const el = this.$refs.chart
      // The chart is kept (with its zoom) while the Readings view is up, and
      // redrawn when it comes back
      if (!el || !this.ds || this.view !== 'chart') return
      const Plotly = await loadPlotly()
      this.plotly = Plotly

      const used = new Set(this.plotted.filter(p => this.activeAxes.includes(p.group)).map(p => p.group))
      const ordered = AXIS_ORDER.filter(g => used.has(g))

      const traces = this.plotted
        .filter(p => used.has(p.group))
        .map(p => {
          const axisIdx = ordered.indexOf(p.group)
          return {
            x: this.ds.timestamps,
            y: this.ds.series[p.key],
            name: p.name,
            type: 'scatter',
            mode: 'lines',
            connectgaps: true,
            line: { color: p.color, width: 2, dash: p.dash || 'solid' },
            yaxis: axisIdx === 0 ? 'y' : `y${axisIdx + 1}`,
            hovertemplate: `%{y:.2f} ${p.unit || ''}<extra>${p.name}</extra>`
          }
        })

      const layout = buildLayout(ordered, { compact: this.compact })
      layout.shapes = this.cursorShapes()
      // The series arrays grow in place as readings stream in, and Plotly.react
      // treats an array it has already seen as unchanged. A new datarevision
      // is what tells it to redraw; without it the chart freezes at its first
      // render while the values panel keeps updating.
      layout.datarevision = this.ds.timestamps.length

      const first = !this.chartReady
      await Plotly.react(el, traces, layout, {
        responsive: true,
        displayModeBar: !this.compact,
        displaylogo: false,
        modeBarButtonsToRemove: ['lasso2d', 'select2d']
      })
      if (first) {
        this.chartReady = true
        // Zooming or panning by hand hands the range to the viewer: stop
        // following the latest readings until they pick a range mode again.
        el.on('plotly_relayout', (e) => {
          if (this.programmaticRelayout) return
          if (e['xaxis.range[0]'] !== undefined || e['xaxis.range'] !== undefined) this.rangeMode = 'custom'
          if (e['xaxis.autorange']) this.rangeMode = 'all'
        })
        // Clicking a point puts the time cursor there; every trace shares the
        // dataset's timestamps, so the point's index is the reading's
        el.on('plotly_click', (e) => {
          const point = e.points && e.points[0]
          if (point && Number.isInteger(point.pointIndex)) this.seek(point.pointIndex)
        })
      }
      this.applyRange()
    },
    applyRange () {
      const el = this.$refs.chart
      if (!el || !this.plotly || !this.ds || !this.ds.timestamps.length) return
      let update = null
      if (this.rangeMode === 'all') {
        update = { 'xaxis.autorange': true }
      } else if (this.rangeMode === 'recent') {
        const end = this.ds.timestamps[this.ds.timestamps.length - 1]
        const endMs = new Date(end.replace(' ', 'T') + 'Z').getTime()
        const start = new Date(endMs - RECENT_WINDOW_MS).toISOString().replace('T', ' ').slice(0, 19)
        update = { 'xaxis.range': [start, end] }
      }
      if (!update) return
      this.programmaticRelayout = true
      this.plotly.relayout(el, update).finally(() => { this.programmaticRelayout = false })
    }
  }
}
</script>

<style scoped lang="scss">
// Palette and proportions of the approved HVAC lab dashboard.
.lab-dash {
  --lab-bg: #f0f2f5;
  --lab-surface: #fff;
  --lab-text: #1a1a2e;
  --lab-muted: #6b7280;
  --lab-border: #e2e5ea;
  --lab-accent: #1e3a5f;
  --lab-header-bg: #1a2332;
  --lab-header-text: #e8eaf0;
  --lab-sidebar: #f8f9fb;
  --lab-live: #dc2626;

  display: flex;
  flex-direction: column;
  height: 100%;
  // On a short screen with the CUSTOM picker open, the page scrolls rather
  // than clipping the picker's last rows.
  overflow-y: auto;
  background: var(--lab-bg);
  color: var(--lab-text);
  font-size: 13px;

  // Rows keep their natural height so overflow becomes scrolling. Left to
  // shrink, the wrapped chip row is squashed and its second line of chips
  // spills over the tab bar, swallowing clicks on the tabs.
  > .lab-dash-header,
  > .lab-dash-stopbar,
  > .lab-dash-meta,
  > .lab-dash-tabs {
    flex-shrink: 0;
  }
}

.lab-dash-header {
  background: var(--lab-header-bg);
  color: var(--lab-header-text);
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.lab-dash-title {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  h2 {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.3px;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.lab-dash-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 9px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;

  &.live { background: var(--lab-live); color: #fff; }
  &.replay { background: #b45309; color: #fff; }
  &.done { background: #334155; color: #cbd5e1; }
  &.failed { background: #7f1d1d; color: #fecaca; }
  &.file { background: #1e40af; color: #dbeafe; }
  &.playback { background: #b45309; color: #fff; }

  .pulse {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fff;
    animation: lab-pulse 1.4s ease-in-out infinite;
  }
}

@keyframes lab-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.75); }
}

.lab-dash-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.lab-dash-fresh,
.lab-dash-provenance {
  font-size: 11px;
  color: #9ca3af;
  font-variant-numeric: tabular-nums;
}

.lab-dash-provenance {
  font-family: ui-monospace, Consolas, monospace;
}

.lab-dash-hbtn {
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  color: var(--lab-header-text);
  background: transparent;
  border: 1px solid #475569;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: #334155; border-color: #64748b; }
  &:focus-visible { outline: 2px solid #60a5fa; outline-offset: 2px; }

  &.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;

    &:hover { background: #1d4ed8; border-color: #1d4ed8; }
  }
}

// Appears under the header when a watched stream stops
.lab-dash-stopbar {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 20px;
  font-size: 12px;
  border-bottom: 1px solid;

  &.ended { background: #ecfdf5; border-color: #a7f3d0; color: #065f46; }
  &.stalled { background: #fffbeb; border-color: #fde68a; color: #92400e; }

  .msg { font-weight: 600; }
  .hint { color: var(--lab-muted); }
  .hint.error { color: #b91c1c; }
}

.stopbar-btn {
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  white-space: nowrap;

  &:hover { background: rgba(255, 255, 255, 0.6); }
  &.primary { background: #2563eb; border-color: #2563eb; color: #fff; }
  &.primary:hover { background: #1d4ed8; }
  &:focus-visible { outline: 2px solid #60a5fa; outline-offset: 2px; }
}

.stopbar-dismiss {
  margin-left: auto;
  background: none;
  border: none;
  color: inherit;
  font-size: 18px;
  line-height: 1;
  opacity: 0.6;

  &:hover { opacity: 1; }
}

.lab-dash-close {
  background: none;
  border: none;
  color: var(--lab-header-text);
  font-size: 24px;
  line-height: 1;
  padding: 0 4px;
  cursor: pointer;

  &:hover { color: #fff; }
  &:focus-visible { outline: 2px solid #60a5fa; outline-offset: 2px; }
}

.lab-dash-meta {
  padding: 8px 20px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.meta-chip {
  background: var(--lab-surface);
  padding: 5px 12px;
  border-radius: 16px;
  border: 1px solid var(--lab-border);
  font-size: 11px;
  white-space: nowrap;

  .lbl { color: var(--lab-muted); margin-right: 4px; }
  .val { font-weight: 600; font-variant-numeric: tabular-nums; }
}

.lab-dash-tabs {
  padding: 0 20px;
  background: var(--lab-surface);
  border-bottom: 1px solid var(--lab-border);
  display: flex;
  align-items: center;
  min-height: 40px;

  .tab {
    padding: 9px 20px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    color: var(--lab-muted);
    letter-spacing: 0.3px;
    white-space: nowrap;
    transition: all 0.15s;

    &:hover { color: var(--lab-text); }
    &.active { color: var(--lab-accent); border-bottom-color: var(--lab-accent); }
  }
}

.tab-list {
  display: flex;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar { display: none; }
}

.tab-list-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--lab-muted);
  letter-spacing: 0.3px;
}

.lab-dash-tools {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-left: 12px;
  flex-shrink: 0;
}

.view-switch {
  display: flex;
  border: 1px solid var(--lab-accent);
  border-radius: 4px;
  overflow: hidden;
}

.view-btn {
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  color: var(--lab-accent);
  background: var(--lab-surface);
  border: none;
  cursor: pointer;
  white-space: nowrap;

  & + & { border-left: 1px solid var(--lab-accent); }
  &.on { background: var(--lab-accent); color: #fff; }
  &:focus-visible { outline: 2px solid #60a5fa; outline-offset: -2px; }
}

.lab-dash-range {
  display: flex;
  gap: 4px;
}

.range-btn {
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid var(--lab-border);
  background: var(--lab-surface);
  color: var(--lab-muted);
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;

  &.on { background: var(--lab-accent); border-color: var(--lab-accent); color: #fff; }
}

.lab-dash-main {
  display: flex;
  flex: 1 0 420px;
}

.lab-dash-chart-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.lab-dash-chart-wrap {
  flex: 1;
  position: relative;
  // Flex items default to min-height: auto and would refuse to shrink below
  // the plot's pixel height; the chart then spills over whatever is below.
  min-height: 0;
}

// Absolutely positioned so the plot's own pixel size never feeds back into
// the layout; the ResizeObserver fits the plot to this box instead.
.lab-dash-chart {
  position: absolute;
  inset: 12px 16px 4px;
}

.lab-dash-axes {
  padding: 6px 16px 8px;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;

  .label {
    font-size: 10px;
    color: var(--lab-muted);
    font-weight: 600;
    margin-right: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.axis-btn {
  padding: 3px 10px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.15s;

  &.on { color: #fff; }
  &.off { background: #f5f5f5; color: #999; border-color: #ddd; }
}

.lab-dash-legend {
  width: 260px;
  min-width: 260px;
  background: var(--lab-sidebar);
  border-left: 1px solid var(--lab-border);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.legend-header {
  padding: 10px 12px 6px;
  font-size: 11px;
  font-weight: 700;
  color: var(--lab-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--lab-border);
  display: flex;
  justify-content: space-between;

  .rows {
    font-weight: 400;
    font-size: 10px;
    color: #aaa;
    text-transform: none;
    letter-spacing: 0;
    font-variant-numeric: tabular-nums;
  }
}

.legend-table { flex: 1; overflow-y: auto; }

.legend-row {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  border-bottom: 1px solid #f0f1f3;
  gap: 8px;
  transition: background 0.6s ease;

  &:hover { background: #eef1f5; }
  &.flash { background: #fef3c7; transition: none; }
}

.legend-dot { width: 10px; height: 10px; border-radius: 2px; flex-shrink: 0; }

.legend-name {
  flex: 1;
  font-size: 11px;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.legend-val {
  font-size: 12px;
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.legend-unit {
  font-size: 10px;
  color: var(--lab-muted);
  text-align: right;
  min-width: 28px;
}

.legend-none {
  padding: 16px;
  color: #999;
  font-size: 12px;
}

.lab-dash-params {
  padding: 12px 20px;
  background: var(--lab-surface);
  border-top: 1px solid var(--lab-border);
  max-height: 280px;
  overflow-y: auto;
  flex-shrink: 0;

  h3 {
    font-size: 11px;
    font-weight: 700;
    color: var(--lab-accent);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 8px;
  }
}

.param-groups {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}

.param-grp-title {
  font-size: 10px;
  font-weight: 700;
  color: var(--lab-muted);
  text-transform: uppercase;
  margin-bottom: 4px;
  padding-bottom: 3px;
  border-bottom: 1px solid var(--lab-border);
}

.param-check {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 2px 4px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;

  &:hover { background: #eef1f5; }
  &.inactive { opacity: 0.5; }
  input { accent-color: var(--lab-accent); }
  .pu { color: var(--lab-muted); font-size: 10px; }
}

.lab-dash-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 200px;
  color: var(--lab-muted);
  font-size: 14px;
  padding: 24px;
  text-align: center;

  &.error { color: #b91c1c; }

  &.overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    min-height: 0;
  }
}

// Shown in place of the dashboard when the service turns someone away
.lab-dash-blocked {
  align-self: center;
  margin: 12vh 16px 0;
  max-width: 420px;
  padding: 28px 32px;
  text-align: center;
  background: var(--lab-surface);
  border: 1px solid var(--lab-border);
  border-radius: 8px;

  h3 {
    margin: 0 0 8px;
    font-size: 17px;
    font-weight: 700;
    color: var(--lab-accent);
  }

  p {
    margin: 0 0 20px;
    font-size: 14px;
    line-height: 1.5;
    color: var(--lab-muted);
  }
}

.blocked-btn {
  padding: 7px 20px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: var(--lab-accent);
  border: none;
  border-radius: 4px;

  &:focus-visible { outline: 2px solid #60a5fa; outline-offset: 2px; }
}

// Tablets and narrow windows: the values panel moves under the chart
@media (max-width: 900px) {
  .lab-dash-main {
    flex-direction: column;
    flex: 0 0 auto;
  }

  .lab-dash-chart-wrap {
    flex: none;
    height: 52vh;
    min-height: 300px;
  }

  .lab-dash-legend {
    width: auto;
    min-width: 0;
    max-height: 280px;
    border-left: none;
    border-top: 1px solid var(--lab-border);
  }
}

// Phones
@media (max-width: 640px) {
  .lab-dash { font-size: 14px; }

  // The close button keeps its corner whatever wraps below it
  .lab-dash-header {
    position: relative;
    padding: 8px 48px 8px 12px;
    gap: 6px;
  }

  .lab-dash-title h2 { font-size: 14px; }

  .lab-dash-header-right {
    width: 100%;
    gap: 10px;
  }

  .lab-dash-provenance { display: none; }

  .lab-dash-close {
    position: absolute;
    top: 6px;
    right: 8px;
    padding: 4px 8px;
  }

  .lab-dash-hbtn { padding: 6px 12px; }

  .lab-dash-stopbar {
    flex-wrap: wrap;
    padding: 8px 12px;

    .msg { flex-basis: calc(100% - 32px); }
  }

  .stopbar-dismiss {
    position: absolute;
    right: 8px;
  }

  // One swipeable row instead of three wrapped ones
  .lab-dash-meta {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 8px 12px;
    scrollbar-width: none;

    &::-webkit-scrollbar { display: none; }
  }

  .lab-dash-tabs {
    flex-wrap: wrap;
    padding: 0 12px;

    .tab { padding: 10px 12px; }
  }

  .tab-list,
  .tab-list-title {
    flex-basis: 100%;
  }

  .tab-list-title { padding: 10px 0 2px; }

  .lab-dash-tools {
    flex-basis: 100%;
    justify-content: space-between;
    padding: 6px 0 8px;
  }

  .view-btn,
  .range-btn { padding: 7px 12px; }

  .lab-dash-chart { inset: 8px 4px 4px; }

  .lab-dash-axes { padding: 6px 12px 8px; }

  .axis-btn { padding: 5px 10px; }

  .legend-row { padding: 7px 12px; }

  .lab-dash-params {
    padding: 12px;
    max-height: none;
  }

  .param-check { padding: 5px 4px; }
}

@media (prefers-reduced-motion: reduce) {
  .lab-dash-badge .pulse { animation: none; }
  .legend-row { transition: none; }
}
</style>
